import * as React from "react";
import { useLocation, useParams } from "react-router-dom";
import { AlertMessage, AlertType, ButtonType, CustomButton, SpinnerWrapper } from "soprasteria-ui-library";
import { useAuth } from "../../../App";
import { ClinicApi } from "../../../api/ClinicApi";
import { OrganisationApi } from "../../../api/OrganisationApi";
import { IClinic, IClinicTab } from "../../../models/IClinic";
import { IOrganisation } from "../../../models/IOrganisation";
import { NavigateBackFooter } from "../../NavigateBackFooter/NavigateBackFooter";
import { ClinicInformation } from "./ClinicInformation";
import { ClinicTabs } from "./Tabs/ClinicTabs";
import { UserContext, isInRole } from "../../../contexts/UserContext";
import { MENUETTPORTALVA } from "../../../utils/RoleConstants";
import { setMessage } from "../../../helpers/MessageHelper";
import { EditClinicModal } from "./EditClinicModal";

export const Clinic: React.FC<{}> = () => {
    const auth = useAuth();
    const api = new ClinicApi(auth);
    const organisationApi = new OrganisationApi(auth);
    const userInfo = React.useContext(UserContext);
    const hasRoleVA = isInRole(MENUETTPORTALVA, userInfo.roles ? userInfo.roles : []);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [successMessage, setSuccessMessage] = React.useState<string>("");
    const [clinic, setClinic] = React.useState<IClinic>();
    const [organisation, setOrganisation] = React.useState<IOrganisation>();
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const [selectedTab, setSelectedTab] = React.useState<IClinicTab>();
    const [renderModal, setRenderModal] = React.useState<boolean>(false);

    React.useEffect(() => {
        loadClinic(parseInt(id));
    }, [, location]);

    const loadClinic = (clinicId: number) => {
        setIsLoading(true);
        api.getClinicByClinicId(clinicId, (result) => {
            setClinic(result);
            loadOrganisation(result.organisationId);
        }, (error) => { setErrorMessage(error) }, () => { setIsLoading(false); });
    }

    const loadOrganisation = (organisationId: number) => {
        setIsLoading(true);
        organisationApi.getOrganisationById(organisationId, (result) => {
            setOrganisation(result);
        }, (error) => { setErrorMessage(error) }, () => { setIsLoading(false); });
    }

    const onTabSubmit = (message: string, tab: IClinicTab) => {
        setSuccessMessage(setMessage(message, successMessage));
        if (clinic) {
            const clinicId = clinic.clinicId;
            setClinic(undefined);
            loadClinic(clinicId);
            setSelectedTab(tab);
        }
    }

    const closeModal = () => {
        setRenderModal(false);
    }

    const onSubmit = (clinicId: number) => {
        document.getElementById("close-edit-clinic-modal")?.click();
        loadClinic(clinicId);
        setSuccessMessage(setMessage("Mottagningen har sparats", successMessage));
    }

    return (
        <SpinnerWrapper isLoading={isLoading}>
            <section className="card">
                <div className="card-header">
                    <div className="row">
                        <div className="col">
                            <h1>Mottagning (Enhet)</h1>
                        </div>
                        <div className="col">
                            <div className="text-right">
                                <CustomButton
                                    buttonText="Redigera mottagning"
                                    buttonType={ButtonType.edit}
                                    onClick={() => setRenderModal(true)} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    <div className="col-12">
                        <AlertMessage alertType={AlertType.success} duration={5000} >{successMessage}</AlertMessage>
                        <AlertMessage alertType={AlertType.danger} duration={5000}>{errorMessage}</AlertMessage>
                    </div>
                    {
                        clinic && organisation ?
                            <>
                                <div className="relative">
                                    <div className="row">
                                        <ClinicInformation clinic={clinic} organisation={organisation} />
                                    </div>
                                </div>
                                <div className="row extra-top-margin">
                                    <div className="col-12">
                                        <ClinicTabs clinicId={clinic!.clinicId} selectedTab={selectedTab} onTabSubmit={onTabSubmit} />
                                    </div>
                                </div>
                            </>
                            : null
                    }

                </div>
                {
                    hasRoleVA ?
                        <NavigateBackFooter />
                        : null
                }
                {
                    clinic && organisation && renderModal ?
                        <EditClinicModal clinic={clinic!} closeModal={closeModal} onSubmit={onSubmit} />
                        : null
                }
            </section>
        </SpinnerWrapper>
    );
}
